/* eslint-disable import/prefer-default-export */
import en from "./data/en.json";
import es from "./data/es.json";
import fr from "./data/fr.json";
import zhHans from "./data/zh-hans.json";
import zhHant from "./data/zh-hant.json";

en satisfies Record<string, string>;
es satisfies Record<string, string>;
fr satisfies Record<string, string>;
zhHans satisfies Record<string, string>;
zhHant satisfies Record<string, string>;

type CountryNames = typeof en;
type CountryCode = keyof CountryNames;
export type CountryOption = {
  code: string;
  name: string;
};

/**
 * Our FTL system supports _any_ language, but for statically translated content
 * like native country names, we only support a subset of languages.
 *
 * You may add to this list as needed.
 */
const SUPPORTED_LANGS = {
  en,
  es,
  fr,
  // Country names are a touchy subject, so we provide both variants of
  // written Chinese with the names of countries as they would be written
  // in the **United States.**
  zh: zhHans, // Default to simplified Chinese (more common in the US)
  "zh-hans": zhHans, // Native names in simplified Chinese for a US market.
  "zh-hant": zhHant, // Native names in traditional Chinese - Taiwan, Hong Kong, etc.
} satisfies Record<string, CountryNames>;

type LocaleMap = typeof SUPPORTED_LANGS;
export type LocaleCode = keyof LocaleMap;

const isSupportedLangCode = (langCode: string): langCode is LocaleCode =>
  Object.keys(SUPPORTED_LANGS).includes(langCode);

const getSupportedLangCode = (langCode: string): LocaleCode => {
  const normalizedLangCode = langCode.toLowerCase();
  return isSupportedLangCode(normalizedLangCode) ? normalizedLangCode : "en";
};

/**
 * @param countryCode ISO 3166 two letter country code
 * @param langCode ISO two letter language code (case-insensitive)
 * @returns string - name of country in given language (or English if unsupported)
 */
export const getCountryName = (countryCode: string, langCode: string) => {
  const countryKey = countryCode.toUpperCase() as CountryCode;
  const supportedLangCode = getSupportedLangCode(langCode);
  return SUPPORTED_LANGS[supportedLangCode][countryKey];
};

export const getCountryOptions = (selectedLocale: string): CountryOption[] => {
  const supportedLocale = getSupportedLangCode(selectedLocale);

  return Object.keys(en)
    .filter((code) => code.length === 2)
    .map((code) => ({ code, name: getCountryName(code, selectedLocale) }))
    .sort((a, b) => a.name.localeCompare(b.name, supportedLocale));
};

export { en };
